import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Video } from 'lucide-react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';

/* Technik-Videos (13.10.2026, gesamtabgleich A4): Das Mitglied filmt eine Übung in der App, der Coach schaut und gibt
   Rückmeldung (Push ohne Inhalt). Videos sind verschlüsselt gespeichert und kommen nur mit Anmeldung — als Blob geladen.
   Schalter Standard aus (Karte unter /planer). Löschung nach 90 Tagen. */

const zeit = (iso) => new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const fehlerText = (e, rest) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);

function Abspielen({ vid, videoLaden }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let weg = null; let aktiv = true;
    videoLaden(vid).then((r) => { if (aktiv) { weg = URL.createObjectURL(r.data); setUrl(weg); } }).catch(() => toast.error('Video nicht geladen'));
    return () => { aktiv = false; if (weg) URL.revokeObjectURL(weg); };
  }, [vid, videoLaden]);
  if (!url) return <p className="text-xs text-muted-foreground">Lädt …</p>;
  return <video src={url} controls playsInline className="max-h-96 w-full rounded-md bg-black" />;
}

export default function TechnikVideos({ laden, videoLaden, kommentieren, testId = 'technikvideos' }) {
  const [d, setD] = useState(null);
  const [offen, setOffen] = useState(null);
  const [text, setText] = useState({});
  const lade = useCallback(() => laden().then((r) => setD(r.data)).catch(() => setD(null)), [laden]);
  useEffect(() => { lade(); }, [lade]);
  if (!d || !d.aktiv) return null;
  const senden = async (v) => {
    try { await kommentieren(v.id, text[v.id] || ''); toast.success('Rückmeldung gesendet'); setText((t) => ({ ...t, [v.id]: '' })); lade(); }
    catch (e) { toast.error(fehlerText(e, 'Nicht gesendet')); }
  };
  return (
    <Card className="border-border/60" data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base"><Video className="h-4 w-4 text-primary" /> Technik-Videos
          {d.videos.filter((v) => v.status === 'neu').length > 0 && <Badge className="border-transparent bg-primary text-[10px] text-primary-foreground hover:bg-primary">{d.videos.filter((v) => v.status === 'neu').length} warten auf Rückmeldung</Badge>}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{d.einwilligung?.erteilt ? 'Vom Mitglied in der App hochgeladen; nach 90 Tagen gelöscht.' : 'Das Mitglied hat (noch) nicht eingewilligt.'}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {!d.videos.length && <p className="text-sm text-muted-foreground">Noch keine Videos.</p>}
        {d.videos.map((v) => (
          <div key={v.id} className="space-y-2 border-t border-border/50 pt-3 first:border-t-0 first:pt-0">
            <p className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-semibold">{v.uebung}</span>
              <span className="text-xs text-muted-foreground">{zeit(v.erstellt_am)}</span>
              <Badge variant={v.status === 'neu' ? 'default' : 'secondary'} className="text-[10px]">{v.status === 'neu' ? 'neu' : 'beantwortet'}</Badge>
              <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setOffen(offen === v.id ? null : v.id)}>{offen === v.id ? 'Schließen' : 'Ansehen'}</Button>
            </p>
            {v.notiz && <p className="text-sm text-muted-foreground break-words">„{v.notiz}“</p>}
            {offen === v.id && <Abspielen vid={v.id} videoLaden={videoLaden} />}
            {v.kommentar && <p className="rounded-md bg-muted/60 px-3 py-2 text-sm break-words"><span className="text-xs text-muted-foreground">{v.kommentar.name}: </span>{v.kommentar.text}</p>}
            {offen === v.id && (
              <div className="flex items-end gap-2">
                <Textarea aria-label="Rückmeldung zur Technik" rows={2} maxLength={2000} className="text-base sm:text-sm" placeholder="Was gut ist, was du ändern würdest …"
                  value={text[v.id] || ''} onChange={(e) => setText((t) => ({ ...t, [v.id]: e.target.value }))} />
                <Button className="h-11" disabled={(text[v.id] || '').trim().length < 2} onClick={() => senden(v)}>Senden</Button>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function TechnikVideosCrm({ leadId }) {
  const f = useMemo(() => ({
    laden: () => api.get(`/technikvideos/mitglied/${leadId}`),
    videoLaden: (vid) => api.get(`/technikvideos/mitglied/${leadId}/${vid}`, { responseType: 'blob' }),
    kommentieren: (vid, text) => api.post(`/technikvideos/mitglied/${leadId}/${vid}/kommentar`, { text }),
  }), [leadId]);
  return <TechnikVideos key={leadId} {...f} />;
}

/* Schalter unter /planer (nur Leitung schaltet). */
export function TechnikVideosSchalter() {
  const [s, setS] = useState(null);
  const [auf, setAuf] = useState(false);
  useEffect(() => { if (auf) api.get('/technikvideos/schalter').then((r) => setS(r.data)).catch(() => setS(null)); }, [auf]);
  const setzen = async (body, ok) => {
    try { const r = await api.put('/technikvideos/schalter', body); setS(r.data); toast.success(ok); } catch (e) { toast.error(fehlerText(e, 'Nicht gespeichert')); }
  };
  return (
    <Card className="border-border/60 print:hidden" data-testid="technikvideos-schalter">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <div>
          <CardTitle className="text-base">Technik-Videos</CardTitle>
          <p className="text-xs text-muted-foreground">Standard aus. Erst den Einwilligungstext prüfen (Anwalt) und freigeben, dann einschalten.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setAuf((v) => !v)} aria-expanded={auf}>{auf ? 'Zuklappen' : 'Öffnen'}</Button>
      </CardHeader>
      {auf && s && (
        <CardContent className="space-y-3">
          <p className="text-sm">Stand: <b>{s.aktiv ? 'eingeschaltet' : 'aus'}</b>{s.freigegeben && ` · Text freigegeben von ${s.freigegeben.von}`}</p>
          {s.einwilligung.entwurf && <p className="text-xs font-semibold text-destructive">ENTWURF – nicht anwaltlich geprüft.</p>}
          <details><summary className="cursor-pointer text-sm font-semibold">{s.einwilligung.titel} ({s.einwilligung.version})</summary>
            <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">{s.einwilligung.text}</p></details>
          <div className="flex flex-wrap gap-2">
            {!s.text_freigegeben && <Button size="sm" variant="outline" onClick={() => window.confirm('Genau diese Fassung freigeben?') && setzen({ text_freigeben_version: s.einwilligung.version }, 'Text freigegeben')}>Text freigeben</Button>}
            <Button size="sm" disabled={!s.text_freigegeben} onClick={() => setzen({ aktiv: !s.aktiv }, s.aktiv ? 'Ausgeschaltet' : 'Eingeschaltet')}>{s.aktiv ? 'Ausschalten' : 'Einschalten'}</Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
